import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence, 
  browserLocalPersistence,
  getAuth,
  User,
  UserCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  vehicles: Array<{
    make: string;
    model: string;
    type: string;
  }>;
  subscriptionTier: 'free' | 'premium';
  createdAt: number;
  lastLogin: number;
}

class AuthService {
  private currentUser: User | null = null;
  private userProfile: UserProfile | null = null;
  private loaded: boolean = false;
  private sessionTimeout: NodeJS.Timeout | null = null;
  private readonly SESSION_TIMEOUT = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
  private initialized = false;
  private cachedToken: string | null = null;
  private tokenExpiryTime: number | null = null;
  private readonly TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry

  constructor() {
    // Set up auth state listener
    onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      if (user) {
        await this.loadUserProfile(user.uid);
        this.setupSessionTimeout();
      } else {
        this.userProfile = null;
        this.clearSessionTimeout();
        this.clearTokenCache(); // Clear token cache when user signs out
      }
      this.loaded = true;
    });
  }

  private setupSessionTimeout() {
    this.clearSessionTimeout();
    this.sessionTimeout = setTimeout(async () => {
      console.log('Session timeout - signing out');
      await this.signOut();
    }, this.SESSION_TIMEOUT);
  }

  private clearSessionTimeout() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  /**
   * Initialize the auth service and wait for Firebase auth state to load
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    return new Promise((resolve) => {
      onAuthStateChanged(auth, async (user) => {
        this.currentUser = user;
        if (user) {
          await this.loadUserProfile(user.uid);
          this.setupSessionTimeout();
        } else {
          this.userProfile = null;
          this.clearSessionTimeout();
          this.clearTokenCache(); // Clear token cache when user signs out
        }
        this.loaded = true;
        this.initialized = true;
        resolve();
      });
    });
  }

  /**
   * Sign up a new user with email and password
   * @param email User's email
   * @param password User's password
   * @param displayName Optional display name
   * @returns User credential
   */
  async signUp(email: string, password: string, displayName?: string): Promise<UserCredential> {
    try {
      // First create the user
      await setPersistence(auth, browserLocalPersistence)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      try {
        // Then create the user profile in Firestore
        const userProfile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: displayName || null,
          vehicles: [],
          subscriptionTier: 'free',
          createdAt: Date.now(),
          lastLogin: Date.now()
        };
        
        await this.saveUserProfile(userProfile);
        this.userProfile = userProfile;
      } catch (firestoreError) {
        console.error('Error creating user profile:', firestoreError);
        // If Firestore fails, delete the user to maintain consistency
        await user.delete();
        throw new Error('Failed to create user profile. Please try again.');
      }
      
      return userCredential;
    } catch (error) {
      console.error('Error in signUp:', error);
      throw error;
    }
  }

  /**
   * Sign in a user with email and password
   * @param email User's email
   * @param password User's password
   * @returns User credential
   */
  async signIn(email: string, password: string): Promise<UserCredential> {
    try {
      await setPersistence(auth, browserLocalPersistence)
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time with a partial write. A full-profile save here
      // would round-trip whatever subscriptionTier is cached in memory, which
      // the security rules (correctly) reject if the backend upgraded the user
      // since the profile was loaded - and that would fail the whole sign-in.
      if (this.userProfile) {
        this.userProfile.lastLogin = Date.now();
        await updateDoc(doc(db, 'users', userCredential.user.uid), { lastLogin: this.userProfile.lastLogin });
      }
      
      return userCredential;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      this.clearTokenCache(); // Clear token cache before signing out
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Get the current user's ID token
   * @returns User's ID token or null if not authenticated
   */
  async getToken(): Promise<string | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      const token = await user.getIdToken(true);
      return token;
    }
    return null;
  }

  /**
   * Get a fresh token, using cached version if still valid
   * @returns User's ID token or null if not authenticated
   */
  async getFreshToken(): Promise<string | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      this.clearTokenCache();
      return null;
    }

    const now = Date.now();
    
    // Check if we have a cached token that's still valid
    if (this.cachedToken && this.tokenExpiryTime && (now + this.TOKEN_REFRESH_BUFFER) < this.tokenExpiryTime) {
      return this.cachedToken;
    }

    try {
      // Get a fresh token
      const token = await user.getIdToken(true);
      
      // Parse the token to get expiry time
      if (token) {
        const payload = this.parseJwtPayload(token);
        if (payload && payload.exp) {
          this.cachedToken = token;
          this.tokenExpiryTime = payload.exp * 1000; // Convert to milliseconds
        }
      }
      
      return token;
    } catch (error) {
      console.error('Error getting fresh token:', error);
      this.clearTokenCache();
      return null;
    }
  }

  /**
   * Parse JWT payload without verification (client-side only for expiry checking)
   * @param token JWT token
   * @returns Parsed payload or null
   */
  private parseJwtPayload(token: string): { exp?: number } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('Error parsing JWT payload:', error);
      return null;
    }
  }

  /**
   * Clear cached token data
   */
  private clearTokenCache(): void {
    this.cachedToken = null;
    this.tokenExpiryTime = null;
  }

    /**
   * Reset user password
   * @param email User's email
   */
    async resetPassword(email: string): Promise<void> {
      try {
        await sendPasswordResetEmail(auth, email);
      } catch (error: any) {
        console.error('Error resetting password:', error);
        throw error;
      }
    }

  /**
   * Load user profile from Firestore
   * @param uid User ID
   */
  private async loadUserProfile(uid: string): Promise<void> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        this.userProfile = userDoc.data() as UserProfile;
      } else {
        console.warn('User profile not found for:', uid);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  /**
   * Save user profile to Firestore
   * @param profile User profile to save
   */
  private async saveUserProfile(profile: UserProfile): Promise<void> {
    try {
      await setDoc(doc(db, 'users', profile.uid), profile);
      this.userProfile = profile;
    } catch (error) {
      console.error('Error saving user profile:', error);
      throw error;
    }
  }

  /**
   * Get the current user
   * @returns Current user or null if not authenticated
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Get the current user profile
   * @returns Current user profile or null if not authenticated
   */
  getUserProfile(): UserProfile | null {
    return this.userProfile;
  }

  /**
   * Check if the user is authenticated
   * @returns True if the user is authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Check if the user profile is loaded
   * @returns 
   */
  get isLoaded(): boolean {
    return this.loaded;
  }
}

// Create a singleton instance
export const authService = new AuthService();